<template>
  <swiper
      slides-per-view="auto"
      class="grid-swiper"
      :watch-overflow="true"
      :direction="'horizontal'"
      @swiper="useSwiper"
  >

    <swiper-slide v-if="slides" v-for="(slide, i) in slides" :key="`slide_${i}`" class="grid-swiper__slide">
      <template v-for="(card, j) in slide" v-if="i <= maxSlides" :key="`card_${i}_${j}`">
        <component :is="useGridCard(card).component"
                   :data="card" v-bind="useGridCard(card).props"
                   class="grid-swiper__component"
                   :class="{
                              'grid-swiper__component--show-component': slides && slides.length > 0 && state.mounted
                           }"
                   :style="`transition-delay: ${65 * (i + j)}ms`"/>
      </template>
    </swiper-slide>

    <swiper-slide class="grid-swiper__slide grid-swiper__slide--show-more-button" v-if="computedShowMoreBtn">
      <button @click="onButtonClick" class="grid-swiper__show-more-button" aria-label="alles bekijken">
        <span class="grid-swiper__label">Alles</span><span class="grid-swiper__show-more-button-label">Bekijken</span>
      </button>
    </swiper-slide>

    <AppSwiperControls/>
  </swiper>
</template>

<script setup lang="ts">

import {Swiper, SwiperSlide} from 'swiper/vue'
import 'swiper/css'
import {navigateTo} from 'nuxt/app'
import {useSwiper} from '~/composables/useSwiper'
import {computed} from "vue";
import {usePageStore} from "~/store/page";
import {TGridBlockFilter} from "~/types/grid-block-types";

const maxSlides = 12

const props = defineProps<{
  slides: any[] | undefined,
  tags?: any[],
  filters?: TGridBlockFilter[]
  title?: String,
  variant: String,
}>()

const state = reactive({
  mounted: false
})

const onButtonClick = () => {

  const searchParams = new URLSearchParams()
  const pageStore = usePageStore()
  const currentPage = pageStore.getCurrentPage

  if (props.filters) {
    searchParams.append('f', JSON.stringify(props.filters))
  }

  if (currentPage) {
    searchParams.append('s', JSON.stringify({
      id: currentPage.id,
      collection: currentPage.collection
    }))
  }

  if (props.title) {
    searchParams.append('t', props.title)
  }

  if (props.tags) {
    searchParams.append('t', props.tags.join('<span class="block heading-h5 lowercase">en</span>'))
  }

  if (props.variant === 'new' || props.variant === 'custom') {
    // sorted by tag/date, we have a special page for that
    navigateTo(`/filter/?${searchParams}`)
  } else {
    navigateTo('/search/')
  }
}

const computedShowMoreBtn = computed(() => {
  // only show button if there might be more to see.
  return (props.variant !== 'other' && props.slides?.length === 12)
})

watch(() => props.slides, () => {
  if (props.slides && props.slides.length > 0 && !state.mounted) {
    nextTick(() => {
      state.mounted = true
    })
  }
})

onMounted(() => {
  if (props.slides && props.slides.length > 0 && !state.mounted) {
    state.mounted = true
  }
})

</script>

<style scoped lang="postcss">
.grid-swiper {
  margin-top: calc(-1 * var(--box-shadow-hover)) !important;
  margin-bottom: calc(-1 * var(--box-shadow-hover)) !important;

  &__slide {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 0.75rem;
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    width: 326px;

    &--show-more-button {
      display: flex;
      height: auto;
      align-items: flex-start;
      justify-content: center;
      width: 95px;
    }
  }

  &__show-more-button {
    @mixin app-box-shadow-with-hover;
    display: flex;
    transform: rotate(-90deg);
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-width: 3px;
    border-color: var(--color-blue-aqua);
    background-color: var(--color-blue-prussian);
    font-family: Midnight, sans-serif;
    text-transform: uppercase;
    line-height: 0.9;
    color: var(--color-blue-aqua);
    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 300ms;
    height: 76px;
    width: 255px;
    border-radius: 50px;
    margin-left: -90px;

    &:hover {
      background-color: var(--color-blue-aqua);
      color: var(--color-blue-prussian);
    }
  }

  &__label {
    margin-left: -3rem;
    margin-top: 0.125rem;
  }

  &__show-more-button-label {
    font-size: 22px;
  }

  & .grid-swiper__component {
    opacity: 0;
    transform: scale(0.8);
    transition: all 250ms ease;
  }

  & .grid-swiper__component--show-component {
    transform: initial;
    opacity: 1;
  }

  & :deep(.swiper-wrapper) {
    transform: none;
    overscroll-behavior: auto;
    padding-top: var(--box-shadow-hover);
    padding-bottom: var(--box-shadow-hover);
  }
}

.swiper-slide:first-child {
  .grid-block-static &,
  .grid-block & {
    margin-left: -14px;
  }
}
</style>
